'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { authApi } from '@/lib/api/auth';
import { useAuthStore } from '@/stores/auth-store';
import {
  ymAuthOpen, ymAuthSuccess, ymRegistration,
  tmrAuthOpen, tmrAuthSuccess, tmrRegistration,
} from '@/lib/analytics';
import { captureEvent } from '@/lib/source-tracker';

// Вход только звонком клиента на наш номер (Call Me). Запасной платный звонок
// с вводом кода убран по решению владельца (10.2026): его баланс пуст с 02.09,
// и при сбое Call Me сайт уводил человека в заведомо мёртвую ветку.
type AuthMethod = 'callme';
type AuthView = 'phone' | 'callme' | 'success';
type AuthBusy = 'request' | 'profile' | null;
type ErrorTarget = 'phone' | 'profile' | null;
type CallStatus = 'waiting' | 'expired' | 'network';

interface ApiErrorInfo {
  message: string;
  status?: number;
  errorCode?: string;
  retryAfter?: number;
}

function readApiError(error: unknown, fallback: string): ApiErrorInfo {
  const response = (error as {
    response?: {
      status?: number;
      headers?: Record<string, unknown>;
      data?: { error?: string; message?: string; error_code?: string; retryAfter?: number };
    };
  })?.response;
  const data = response?.data;
  const retryAfter = Number(data?.retryAfter ?? response?.headers?.['retry-after']);
  return {
    message: data?.error || data?.message || fallback,
    status: response?.status,
    errorCode: data?.error_code,
    retryAfter: Number.isFinite(retryAfter) && retryAfter > 0
      ? Math.ceil(retryAfter)
      : response?.status === 429 ? 60 : undefined,
  };
}

function trackAuth(
  name: 'auth_started' | 'auth_failed' | 'auth_success',
  method: AuthMethod,
  info?: ApiErrorInfo,
) {
  try {
    captureEvent(name, {
      method,
      ...(info ? {
        http_status: info.status,
        error_code: info.errorCode,
        retry_after: info.retryAfter,
      } : {}),
    });
  } catch {
    // Analytics must never interrupt sign-in.
  }
}

function internalRedirect(raw: string | null): string | null {
  if (!raw || !/^\/(?!\/)/.test(raw) || /[\\\u0000-\u001f\u007f]/.test(raw)) return null;
  try {
    const url = new URL(raw, window.location.origin);
    return url.origin === window.location.origin ? url.pathname + url.search + url.hash : null;
  } catch {
    return null;
  }
}

// Сбой сервиса звонков (5xx или нет ответа) обычно разовый: повторяем запрос сами.
const CALLME_RETRY_DELAY_MS = 3000;

function isTransientFailure(info: ApiErrorInfo): boolean {
  return !info.status || info.status >= 500;
}

function wait(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, milliseconds);
    signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
  });
}

function remaining(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/**
 * One mounted flow owns every request, poll and delayed redirect. Closing,
 * changing the phone or unmounting invalidates the whole in-flight session.
 */
export function useAuthFlow() {
  const router = useRouter();
  const isOpen = useAuthStore((state) => state.isAuthModalOpen);
  const [view, setView] = useState<AuthView>('phone');
  const [phone, setPhone] = useState('');
  const [callmeNumber, setCallmeNumber] = useState<string | null>(null);
  const [busy, setBusy] = useState<AuthBusy>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorTarget, setErrorTarget] = useState<ErrorTarget>(null);
  const [callStatus, setCallStatus] = useState<CallStatus>('waiting');
  const [retryDeadline, setRetryDeadline] = useState(0);
  const [callDeadline, setCallDeadline] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const mountedRef = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);
  const busyRef = useRef<AuthBusy>(null);
  const activePhoneRef = useRef('');
  const retryDeadlineRef = useRef(0);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const verifiedRef = useRef(false);
  const profileRetryRef = useRef<{ isNewUser: boolean; method: AuthMethod } | null>(null);
  const redirectRef = useRef<string | null>(null);

  const setPending = useCallback((value: AuthBusy) => {
    busyRef.current = value;
    setBusy(value);
    useAuthStore.getState().setIsLoading(value !== null);
  }, []);

  const cancelPending = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    successTimerRef.current = null;
    pollTimerRef.current = null;
    busyRef.current = null;
    useAuthStore.getState().setIsLoading(false);
  }, []);

  const isCurrent = useCallback((controller: AbortController) =>
    mountedRef.current && controllerRef.current === controller &&
    !controller.signal.aborted && useAuthStore.getState().isAuthModalOpen, []);

  const clearError = useCallback(() => {
    setError(null);
    setErrorTarget(null);
  }, []);

  const recordError = useCallback((info: ApiErrorInfo, target: ErrorTarget) => {
    setError(info.message);
    setErrorTarget(target);
    if (info.retryAfter) {
      const deadline = Math.max(retryDeadlineRef.current, Date.now() + info.retryAfter * 1000);
      retryDeadlineRef.current = deadline;
      setRetryDeadline(deadline);
    }
    setNow(Date.now());
  }, []);

  const resetVerification = useCallback(() => {
    cancelPending();
    setBusy(null);
    setView('phone');
    setCallmeNumber(null);
    setCallDeadline(0);
    setCallStatus('waiting');
    verifiedRef.current = false;
    profileRetryRef.current = null;
    clearError();
    useAuthStore.getState().clearVerificationData();
    useAuthStore.getState().setAuthModalView('login');
  }, [cancelPending, clearError]);

  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = useAuthStore.subscribe((state, previous) => {
      if (!state.isAuthModalOpen && previous.isAuthModalOpen) resetVerification();
      if (state.isAuthModalOpen && !previous.isAuthModalOpen) setNow(Date.now());
    });
    return () => {
      mountedRef.current = false;
      unsubscribe();
      cancelPending();
    };
  }, [cancelPending, resetVerification]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') !== 'required') return;
    redirectRef.current = internalRedirect(params.get('redirect'));
    useAuthStore.getState().openAuthModal();
    params.delete('auth');
    params.delete('redirect');
    const query = params.toString();
    window.history.replaceState(window.history.state, '',
      window.location.pathname + (query ? '?' + query : '') + window.location.hash);
  }, []);

  useEffect(() => {
    const onLogout = () => {
      useAuthStore.getState().setUser(null);
      useAuthStore.getState().setIsAuthenticated(false);
    };
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    try { ymAuthOpen(); tmrAuthOpen(); } catch { /* Metrics do not block the dialog. */ }
  }, [isOpen]);

  // Recompute from wall-clock deadlines, including return from a background tab.
  useEffect(() => {
    if (!isOpen) return;
    const tick = () => setNow(Date.now());
    const timer = setInterval(tick, 1000);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [isOpen]);

  const finishAuth = useCallback(async (
    controller: AbortController, isNewUser: boolean, method: AuthMethod,
  ) => {
    if (!isCurrent(controller)) return;
    verifiedRef.current = true;
    profileRetryRef.current = { isNewUser, method };
    setCallDeadline(0);
    clearError();
    setPending('profile');
    try {
      const profile = await authApi.getProfile(controller.signal);
      if (!isCurrent(controller)) return;
      useAuthStore.getState().setUser(profile);
      useAuthStore.getState().setIsAuthenticated(true);
      profileRetryRef.current = null;
      setView('success');
      setPending(null);
      try {
        ymAuthSuccess(isNewUser);
        tmrAuthSuccess();
        if (isNewUser) { ymRegistration(); tmrRegistration(); }
      } catch { /* Profile success is independent of metrics. */ }
      trackAuth('auth_success', method);
      toast.success(isNewUser ? 'Добро пожаловать! 🎉' : 'Вы успешно вошли');
      successTimerRef.current = setTimeout(() => {
        if (!isCurrent(controller)) return;
        const target = redirectRef.current || '/profile';
        redirectRef.current = null;
        useAuthStore.getState().closeAuthModal();
        router.push(target);
      }, 1200);
    } catch (cause) {
      if (!isCurrent(controller)) return;
      recordError(readApiError(cause, 'Не удалось загрузить профиль. Попробуйте ещё раз.'), 'profile');
      setPending(null);
    }
  }, [clearError, isCurrent, recordError, router, setPending]);

  useEffect(() => {
    if (!isOpen || view !== 'callme' || !callDeadline) return;
    const controller = controllerRef.current;
    if (!controller) return;
    const expire = () => {
      if (!isCurrent(controller) || verifiedRef.current) return;
      cancelPending();
      setBusy(null);
      setCallStatus('expired');
      setCallDeadline(0);
      setNow(Date.now());
    };
    const expirationTimer = setTimeout(expire, Math.max(0, callDeadline - Date.now()));
    const checkExpiry = () => { if (Date.now() >= callDeadline) expire(); };
    document.addEventListener('visibilitychange', checkExpiry);
    window.addEventListener('focus', checkExpiry);

    // Schedule after the previous response: slow requests never overlap.
    const poll = async () => {
      if (!isCurrent(controller) || verifiedRef.current) return;
      if (Date.now() >= callDeadline) { expire(); return; }
      try {
        const result = await authApi.checkCallMe(phone, controller.signal);
        if (!isCurrent(controller)) return;
        if (Date.now() >= callDeadline) { expire(); return; }
        if (result.verified && result.accessToken) {
          await finishAuth(controller, !!result.isNewUser, 'callme');
          return;
        }
        if (!result.verified && !result.waiting && result.reason) {
          expire();
          return;
        }
        setCallStatus('waiting');
      } catch {
        if (!isCurrent(controller)) return;
        setCallStatus('network');
      }
      if (isCurrent(controller) && !verifiedRef.current) {
        pollTimerRef.current = setTimeout(poll, 3000);
      }
    };
    pollTimerRef.current = setTimeout(poll, 3000);
    return () => {
      clearTimeout(expirationTimer);
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
      document.removeEventListener('visibilitychange', checkExpiry);
      window.removeEventListener('focus', checkExpiry);
    };
  }, [callDeadline, cancelPending, finishAuth, isCurrent, isOpen, phone, view]);

  const requestCode = useCallback(async (nextPhone: string) => {
    if (!useAuthStore.getState().isAuthModalOpen) return;
    if (!/^79\d{9}$/.test(nextPhone)) {
      recordError({ message: 'Введите российский мобильный номер: +7 9XX XXX-XX-XX' }, 'phone');
      return;
    }
    if (retryDeadlineRef.current > Date.now()) {
      setNow(Date.now());
      return;
    }
    if (busyRef.current && activePhoneRef.current === nextPhone) return;
    cancelPending();
    const controller = new AbortController();
    controllerRef.current = controller;
    activePhoneRef.current = nextPhone;
    verifiedRef.current = false;
    profileRetryRef.current = null;
    setPhone(nextPhone);
    setCallDeadline(0);
    setCallmeNumber(null);
    clearError();
    setPending('request');
    setView('phone');
    useAuthStore.getState().clearVerificationData();
    useAuthStore.getState().setAuthModalView('login');

    trackAuth('auth_started', 'callme');
    try {
      for (let attempt = 1; ; attempt += 1) {
        try {
          const result = await authApi.initiateCallMe(nextPhone, controller.signal);
          if (!isCurrent(controller)) return;
          if (result.success && result.callmeNumber) {
            useAuthStore.getState().setVerificationData(nextPhone, 'call_me');
            useAuthStore.getState().setAuthModalView('callme');
            setCallmeNumber(result.callmeNumber);
            setView('callme');
            setCallStatus('waiting');
            setCallDeadline(Date.now() + 300_000);
            setNow(Date.now());
            return;
          }
          if (result.retryAfter || result.error_code?.startsWith('rate_limit')) {
            throw { response: { status: 429, data: { ...result, error: result.message } } };
          }
          // 2xx без номера — отказ сервиса, только молчаливый.
          throw { response: { status: 503, data: { ...result, error: result.message } } };
        } catch (cause) {
          if (!isCurrent(controller)) return;
          const info = readApiError(cause, 'Не удалось запросить звонок. Попробуйте позже.');
          // Свой номер, частые попытки и прочие 4xx человек исправляет сам — не повторяем.
          if (attempt >= 2 || !isTransientFailure(info)) throw cause;
          await wait(CALLME_RETRY_DELAY_MS, controller.signal);
          if (!isCurrent(controller)) return;
        }
      }
    } catch (cause) {
      if (!isCurrent(controller)) return;
      const info = readApiError(cause, 'Не удалось запросить звонок. Попробуйте позже.');
      recordError(
        isTransientFailure(info)
          ? { ...info, message: 'Сервис звонков сейчас не отвечает. Попробуйте ещё раз через минуту.' }
          : info,
        'phone',
      );
      trackAuth('auth_failed', 'callme', info);
    } finally {
      if (isCurrent(controller)) setPending(null);
    }
  }, [cancelPending, clearError, isCurrent, recordError, setPending]);

  const retryProfile = useCallback(() => {
    const retry = profileRetryRef.current;
    if (!retry || busyRef.current || !useAuthStore.getState().isAuthModalOpen) return;
    cancelPending();
    const controller = new AbortController();
    controllerRef.current = controller;
    void finishAuth(controller, retry.isNewUser, retry.method);
  }, [cancelPending, finishAuth]);

  const back = useCallback(() => {
    resetVerification();
  }, [resetVerification]);

  const close = useCallback(() => {
    useAuthStore.getState().closeAuthModal();
  }, []);

  return {
    isOpen, view, phone, callmeNumber, busy, error, errorTarget,
    retryAfter: remaining(retryDeadline, now),
    callSecondsLeft: remaining(callDeadline, now),
    callStatus,
    requestCode, back, close, retryProfile,
  };
}
